import {
  capitalizeFirstLetter,
  DataTable,
  FormFeedBack,
  MainButton,
  ModalConfirmAction,
  removeCpfMask,
  STATUSPAGE,
} from '@repo/ui';
import { CpfInput } from 'adasi_components';
import {
  GetAllPersonsFromEntityUseCaseInterface,
  GetPersonByCPFUseCaseInterface,
  UnlinkPersonFromEntityUseCaseInterface,
} from 'domain/usecases';
import {
  getPtBrPluralNameFromProfileType,
  getPtBrSingularNameFromProfileType,
  PROFILE_TYPE,
} from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { makePersonsFromEntityListColumns } from 'presentation/app/modules/Entities/common/columns/entity-settings/persons-from-entity-list-columns';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearAddPersonToEntityModalData,
  clearPersonsFromEntityListData,
  loadAddPersonToEntityModalData,
  loadPersonsFromEntityListData,
} from 'presentation/config/store/entities/entities-slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Col, Form, InputGroup, Row } from 'reactstrap';
import * as Yup from 'yup';

import { AddPersonToEntityModal } from './add-person-to-entity-modal';

interface IProps {
  entityId: string;
  profileType: PROFILE_TYPE;
}

export const PersonsTabContent = ({ entityId, profileType }: IProps) => {
  // Hooks
  const { entitiesService } = useService();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonateWindowName = 'rade-impersonate';

  // Constants
  const teamProfileType = capitalizeFirstLetter(
    getPtBrPluralNameFromProfileType(profileType),
  );

  // States
  const { personsFromEntityListData } = useAppSelector(
    (state) => state.Entities,
  );
  const [isAddPersonToEntityModalOpen, setIsAddPersonToEntityModalOpen] =
    useState(false);

  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const selectedRelationshipIdRef = useRef(null);
  const searchedCPFRef = useRef('');
  const [
    requestGetAllPersonsFromEntityStatus,
    setRequestGetAllPersonsFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);
  const [requestGetPersonByCPFStatus, setRequestGetPersonByCPFStatus] =
    useState(STATUSPAGE.DONE);
  const [
    requestUnlinkPersonFromEntityStatus,
    setRequestUnlinkPersonFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);

  const noDataConfig = {
    noDataTitle: `Nenhum ${getPtBrSingularNameFromProfileType(profileType)} vinculado`,
    noDataDescription: `Nenhum ${getPtBrSingularNameFromProfileType(
      profileType,
    )} foi vinculado a esta organização até o momento.`,
  };

  // Methods
  const toggleUnlinkModal = useCallback(() => {
    setIsUnlinkModalOpen((prevState) => !prevState);
  }, []);

  const toggleAddPersonToEntityModal = useCallback(() => {
    setIsAddPersonToEntityModalOpen((prevState) => !prevState);
  }, []);

  const requestGetPersonByCPF = useCallback(
    async (params: GetPersonByCPFUseCaseInterface.Params) => {
      setRequestGetPersonByCPFStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getPersonByCPF(params)
        .then((res) => {
          if (res.name) {
            dispatch(loadAddPersonToEntityModalData(res));
          }
          searchedCPFRef.current = params.cpf;
          validation.resetForm();
          toggleAddPersonToEntityModal();
        })
        .catch((err) => {
          dispatch(clearAddPersonToEntityModalData());
          notifyError(err.message);
        })
        .finally(() => {
          setRequestGetPersonByCPFStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetAllPersonsFromEntity = useCallback(
    async (params: GetAllPersonsFromEntityUseCaseInterface.Params) => {
      setRequestGetAllPersonsFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getAllPersonsFromEntity(params)
        .then((res) => {
          dispatch(loadPersonsFromEntityListData(res));
        })
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearPersonsFromEntityListData());
        })
        .finally(() => {
          setRequestGetAllPersonsFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );
  const requestUnlinkPersonFromEntity = useCallback(
    async (params: UnlinkPersonFromEntityUseCaseInterface.Params) => {
      setRequestUnlinkPersonFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .unlinkPersonFromEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          toggleUnlinkModal();
          requestGetAllPersonsFromEntity({ entityId, profileType });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestUnlinkPersonFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [entityId],
  );

  // Forms Validations
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cpf: '',
    },
    validationSchema: Yup.object({
      cpf: Yup.string().cpfType('CPF inválido').required('Campo é obrigatório'),
    }),
    onSubmit: (values) => {
      requestGetPersonByCPF({
        cpf: removeCpfMask(values.cpf),
        entityId,
        profileType,
      });
    },
  });

  useEffect(() => {
    requestGetAllPersonsFromEntity({ entityId, profileType });
  }, []);

  return (
    <Card className="mt-5">
      <CardBody>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col md={4} className="mb-3">
              <div className="my-2">
                {teamProfileType && (
                  <h4 className="card-title">{teamProfileType}</h4>
                )}
              </div>
              <InputGroup>
                <CpfInput
                  name="cpf"
                  handleChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  inputId="cpf"
                  placeholder="Informe o CPF do profissional para vincular"
                  value={validation.values.cpf}
                  className="form-control"
                  invalid={!!(validation.touched.cpf && validation.errors.cpf)}
                />
                <MainButton
                  type="submit"
                  isDisabled={
                    requestGetPersonByCPFStatus === STATUSPAGE.LOADING
                  }
                  isLoading={requestGetPersonByCPFStatus === STATUSPAGE.LOADING}
                >
                  Vincular por CPF
                </MainButton>
              </InputGroup>
              <FormFeedBack
                error={String(validation.errors?.cpf)}
                isInvalid={
                  !!(validation.touched?.cpf && validation.errors?.cpf)
                }
              />
            </Col>
          </Row>
        </Form>

        <AddPersonToEntityModal
          isOpen={isAddPersonToEntityModalOpen}
          toggle={toggleAddPersonToEntityModal}
          searchedCPFRef={searchedCPFRef}
          entityId={entityId}
          profileType={profileType}
          requestGetAllPersonsFromEntity={requestGetAllPersonsFromEntity}
        />

        <Row>
          <DataTable
            noDataTitle={noDataConfig.noDataTitle}
            noDataDescription={noDataConfig.noDataDescription}
            data={personsFromEntityListData || []}
            actions={[
              {
                id: 1,
                name: 'add',
                label: 'Redirecionar',
                onPress: (person) => {
                  window.open(
                    `/impersonate/${profileType === PROFILE_TYPE.DIRECTOR ? 'gestor' : 'organizacao'}/${person.id}`,
                    impersonateWindowName,
                  );
                },
                nameFeatherIcon: 'lock',
                classNameColorIcon: 'primary',
              },
              {
                id: 1,
                name: 'unlink',
                label: 'Desvincular',
                onPress: (person) => {
                  selectedRelationshipIdRef.current = person.id;
                  toggleUnlinkModal();
                },
                nameFeatherIcon: 'user-minus',
                classNameColorIcon: 'danger',
              },
            ]}
            columns={makePersonsFromEntityListColumns({
              profileType,
              navigate,
            })}
            loading={
              requestGetAllPersonsFromEntityStatus === STATUSPAGE.LOADING
            }
            defaultSortAsc
            defaultSortFieldId={1}
            defaultRowsPerPage={50}
            canSearch
            keysSearch={['name', 'email', 'cpf']}
          />
        </Row>
        <ModalConfirmAction
          loading={requestUnlinkPersonFromEntityStatus === STATUSPAGE.LOADING}
          modalTheme="danger"
          actionBtnLabel="Remover"
          isOpen={isUnlinkModalOpen}
          toggle={toggleUnlinkModal}
          onConfirm={() =>
            requestUnlinkPersonFromEntity({
              id: selectedRelationshipIdRef.current,
              profileType,
              entityId,
            })
          }
          title={`Remover ${capitalizeFirstLetter(getPtBrSingularNameFromProfileType(profileType))}`}
          subtitle="Após a remoção, esta pessoa não poderá mais acessar o RADE com este perfil. Tem certeza de que deseja continuar?"
        />
      </CardBody>
    </Card>
  );
};
